import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "input", "results", "loading", "filters"]
  static values = {
    minLength: { type: Number, default: 3 },
    debounceMs: { type: Number, default: 300 },
    canAccessAdmin: Boolean,
    appHost: String  // Set only on www layout – app-served results (products, projects, invoices, customers) link here
  }

  connect() {
    this.debounceTimer = null
    this.isOpen = false
    this.currentFilter = 'all'
    this.allResults = null
    this.setupKeyboardShortcuts()
    this.updateInvoicesFilterVisibility()
  }

  disconnect() {
    this.removeKeyboardShortcuts()
  }

  open() {
    this.isOpen = true
    this.showOverlay()
    this.focusInput()
  }

  close() {
    this.isOpen = false
    this.hideOverlay()
    this.clearResults()
  }

  closeOnBackdrop(event) {
    // Only close if clicking on the backdrop, not on the content
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  showOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('d-none')
      this.overlayTarget.classList.add('d-block')

      document.body.classList.add('search-overlay-open')
    }
  }

  hideOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('d-block')
      this.overlayTarget.classList.add('d-none')

      document.body.classList.remove('search-overlay-open')
    }
  }

  focusInput() {
    if (this.hasInputTarget) {
      setTimeout(() => {
        this.inputTarget.focus()
      }, 100)
    }
  }

  onInputChange(event) {
    const query = event.target.value.trim()

    // Clear previous debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    if (query.length < this.minLengthValue) {
      this.clearResults()
      return
    }

    // Debounce the search
    this.debounceTimer = setTimeout(() => {
      this.performSearch(query)
    }, this.debounceMsValue)
  }

  async performSearch(query) {
    if (!query || query.length < this.minLengthValue) {
      this.clearResults()
      return
    }

    this.showLoading()

    try {
      const response = await fetch(`/search/overlay?q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.allResults = data
        this.displayResults(data)
      } else {
        this.showError('Search failed. Please try again.')
      }
    } catch (error) {
      console.error('Search error:', error)
      this.showError('Search failed. Please try again.')
    } finally {
      this.hideLoading()
    }
  }

  displayResults(data) {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = this.buildResultsHTML(data)
      this.updateFilterButtons()
    }
  }

  // Inline SVG icons (no Bootstrap Icons dependency) – matches search overlay filter style
  sectionIconSvg(iconKey) {
    const icons = {
      product: '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>',
      project: '<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>',
      'website-page': '<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
      guide: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>',
      'developer-doc': '<path d="M16 18l6-6-6-6"/><path d="M8 6l-6 6 6 6"/>',
      invoice: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>',
      customer: '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>'
    }
    const path = icons[iconKey] || ''
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="0.67" stroke-linecap="round" stroke-linejoin="round" class="icon-thin-sm me-2" style="width: 1rem; height: 1rem;" aria-hidden="true">${path}</svg>`
  }

  buildResultsHTML(data) {
    if (!data || (!data.products?.length && !data.projects?.length && !data.guides?.length && !data.developer_docs?.length && !data.website_pages?.length && !data.invoices?.length && !data.customers?.length)) {
      return '<div class="text-center py-4">No results found</div>'
    }

    let html = '<div class="search-results-content">'

    // Products
    if (data.products?.length && (this.currentFilter === 'all' || this.currentFilter === 'products')) {
      html += this.buildSectionHTML('Products', data.products, 'product', 'product')
    }

    // Projects
    if (data.projects?.length && (this.currentFilter === 'all' || this.currentFilter === 'projects')) {
      html += this.buildSectionHTML('Projects', data.projects, 'project', 'project')
    }

    // Website pages (excluding /support)
    if (data.website_pages?.length && (this.currentFilter === 'all' || this.currentFilter === 'website-pages')) {
      html += this.buildSectionHTML('Website', data.website_pages, 'website-page', 'website-page')
    }

    // Guides
    if (data.guides?.length && (this.currentFilter === 'all' || this.currentFilter === 'guides')) {
      html += this.buildSectionHTML('Guides', data.guides, 'guide', 'guide')
    }

    // Developer Docs
    if (data.developer_docs?.length && (this.currentFilter === 'all' || this.currentFilter === 'developer-docs')) {
      html += this.buildSectionHTML('Developer Docs', data.developer_docs, 'developer-doc', 'developer-doc')
    }

    // Invoices (admin only – backend also restricts; defense in depth)
    if (this.canAccessAdminValue && data.invoices?.length && (this.currentFilter === 'all' || this.currentFilter === 'invoices')) {
      html += this.buildSectionHTML('Invoices', data.invoices, 'invoice', 'invoice')
    }

    // Customers (admin only – never render for non-admins)
    if (this.canAccessAdminValue && data.customers?.length && (this.currentFilter === 'all' || this.currentFilter === 'customers')) {
      html += this.buildSectionHTML('Customers', data.customers, 'customer', 'customer')
    }

    html += '</div>'
    return html
  }

  buildSectionHTML(title, items, iconKey, type) {
    const iconSvg = this.sectionIconSvg(iconKey)
    let html = `
      <div class="search-section mb-3">
        <h6 class="search-section-title mb-2 d-flex align-items-center">
          ${iconSvg}${title}
        </h6>
        <div class="search-section-items">
    `

    items.slice(0, 5).forEach(item => {
      html += this.buildItemHTML(item, type)
    })

    if (items.length > 5) {
      html += `<div class="search-item">
        <small>... and ${items.length - 5} more results</small>
      </div>`
    }

    html += `
        </div>
      </div>
    `
    return html
  }

  buildItemHTML(item, type) {
    const url = this.getItemURL(item, type)
    const title = item.title || item.name || item.product_name || (type === 'invoice' ? `Invoice #${item.invoice_number}` : (type === 'customer' ? item.email : 'Untitled'))
    const subtitle = this.getItemSubtitle(item, type)

    return `
      <a href="${url}" class="search-item d-block text-decoration-none p-2 rounded mb-1" data-action="click->search-overlay#close">
        <div class="fw-medium text-dark">${this.escapeHtml(title)}</div>
        ${subtitle ? `<div class="small text-body-secondary">${this.escapeHtml(subtitle)}</div>` : ''}
      </a>
    `
  }

  getItemURL(item, type) {
    // Only products, projects, invoices, customers live on app; guides, website pages, developer docs stay on www
    const appBase = this.hasAppHostValue ? this.appHostValue : ''

    switch (type) {
      case 'product':
        return `${appBase}/products/${item.uuid}`
      case 'project':
        return `${appBase}/projects/${item.uuid}`
      case 'website-page':
        return item.path || '/'
      case 'guide':
        return `/support/${item.category || 'basics'}/${item.slug}`
      case 'developer-doc':
        return `/support/developer/${item.slug}`
      case 'invoice':
        return `${appBase}/admin/invoices?year=${item.year}&month=${item.month}`
      case 'customer':
        return `${appBase}/admin/users/${item.id}`
      default:
        return '#'
    }
  }

  getItemSubtitle(item, type) {
    switch (type) {
      case 'product':
        return item.manufacturer_name ? `${item.manufacturer_name} • ${item.sku || ''}`.trim() : item.sku
      case 'project':
        return item.project_type || ''
      case 'website-page':
        return item.excerpt || ''
      case 'guide':
      case 'developer-doc':
        return item.excerpt || item.category
      case 'invoice':
        return item.email ? `${item.email} • ${item.month}/${item.year}` : `${item.month}/${item.year}`
      case 'customer':
        return item.email || ''
      default:
        return ''
    }
  }

  clearResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ''
    }
  }

  filterResults(event) {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()

    const filter = event.currentTarget.dataset.filter
    this.currentFilter = filter

    // Update button states
    this.updateFilterButtons()

    // Re-render results with current filter
    if (this.allResults) {
      this.displayResults(this.allResults)
    }

    return false
  }

  updateFilterButtons() {
    if (this.hasFiltersTarget) {
      const buttons = this.filtersTarget.querySelectorAll('.filter-link')
      buttons.forEach(button => {
        const filter = button.dataset.filter

        // Update button states
        if (filter === this.currentFilter) {
          button.classList.add('active')
        } else {
          button.classList.remove('active')
        }

        // Invoices and Customers: only show for users who can access admin
        if (filter === 'invoices' || filter === 'customers') {
          button.style.display = this.canAccessAdminValue ? 'inline-flex' : 'none'
          return
        }

        // Show/hide other buttons based on available results
        if (this.allResults) {
          const hasResults = this.hasResultsForFilter(filter)
          button.style.display = hasResults ? 'inline-flex' : 'none'
        }
      })
    }
  }

  updateInvoicesFilterVisibility() {
    if (this.hasFiltersTarget) {
      const invoicesLink = this.filtersTarget.querySelector('[data-filter="invoices"]')
      const customersLink = this.filtersTarget.querySelector('[data-filter="customers"]')
      if (invoicesLink) {
        invoicesLink.style.display = this.canAccessAdminValue ? 'inline-flex' : 'none'
      }
      if (customersLink) {
        customersLink.style.display = this.canAccessAdminValue ? 'inline-flex' : 'none'
      }
    }
  }

  hasResultsForFilter(filter) {
    if (!this.allResults) return false

    switch (filter) {
      case 'all':
        return this.allResults.products?.length ||
          this.allResults.projects?.length ||
          this.allResults.website_pages?.length ||
          this.allResults.guides?.length ||
          this.allResults.developer_docs?.length ||
          this.allResults.invoices?.length ||
          this.allResults.customers?.length
      case 'products':
        return this.allResults.products?.length > 0
      case 'projects':
        return this.allResults.projects?.length > 0
      case 'website-pages':
        return this.allResults.website_pages?.length > 0
      case 'guides':
        return this.allResults.guides?.length > 0
      case 'developer-docs':
        return this.allResults.developer_docs?.length > 0
      case 'invoices':
        return this.allResults.invoices?.length > 0
      case 'customers':
        return this.allResults.customers?.length > 0
      default:
        return false
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('d-none')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('d-none')
    }
  }

  showError(message) {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = `<div class="text-center text-danger py-4">${message}</div>`
    }
  }

  setupKeyboardShortcuts() {
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)
  }

  removeKeyboardShortcuts() {
    document.removeEventListener('keydown', this.handleKeydown)
  }

  handleKeydown(event) {
    // Open search with Cmd/Ctrl + K
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault()
      this.open()
    }

    // Close search with Escape
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault()
      this.close()
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
